// Wohler Chemical — GABAergic/Anxiolytic + Pain compounds (Programs E variant, pain)

export const gabaPainCompounds = {
  "phenibut": {
    id: "phenibut",
    name: "Phenibut",
    fullName: "β-Phenyl-γ-aminobutyric acid",
    aliases: ["Noofen", "Fenibut"],
    categoryId: "gabaergic",
    program: "Program J / Anxiety",
    cas: "1078-21-3",
    formula: "C₁₀H₁₃NO₂",
    molecularWeight: "179.22 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. FDA has issued warning letters to supplement companies. Significant abuse/dependence potential documented. A few U.S. states have considered scheduling.",
    benefitScore: 65,
    harmScore: 38,
    evidenceConfidence: "HIGH",
    status: "Hold — Known Dependence Risk",
    statusColor: "orange",
    goDecision: "GO with caveats — research-use only with rigorous dependence characterization",
    summary: "GABA-B agonist + α2δ calcium channel modulator. Clinical drug in Russia since 1960s (Noofen). Anxiolytic, nootropic, sleep-promoting. 40+ years of Russian clinical literature. Dependence syndrome similar to baclofen withdrawal — key cautionary point.",
    pros: [
      "40+ years of Russian clinical use — extensive human data",
      "GABA-B + α2δ mechanism — distinct from benzodiazepines",
      "Simple synthesis",
      "Useful research tool for understanding GABA-B anxiolysis"
    ],
    cons: [
      "Documented dependence + withdrawal syndrome",
      "FDA warning letters — regulatory attention",
      "Some state-level scheduling consideration",
      "Low margin of safety vs alcohol (synergistic sedation)"
    ],
    pharmacology: {
      targets: [
        { receptor: "GABA-B", activity: "Agonist", value: "Anxiolytic/sedative" },
        { receptor: "α2δ calcium channel", activity: "Modulator", value: "Analgesic-like + anxiolytic" }
      ],
      duration: "6–8 h",
      mechanism: "GABA-B agonism + α2δ modulation — similar to baclofen + pregabalin combined"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Michael addition of nitromethane to cinnamic acid derivative, followed by reduction and hydrolysis.",
      steps: [
        { step: "1", title: "β-Nitro-γ-phenyl-butyric ester", description: "Michael addition of nitromethane to ethyl cinnamate, base (KOH), ethanol.", solvent: "Ethanol", keyReagents: "Nitromethane, ethyl cinnamate, KOH" },
        { step: "2", title: "Reduction of nitro → amine", description: "H₂/Raney Ni or Zn/HCl in ethanol.", solvent: "Ethanol", keyReagents: "Zn dust, HCl (or H₂/Raney Ni)" },
        { step: "3", title: "Ester hydrolysis", description: "NaOH in water/ethanol; acidify to isozone pH; crystallize phenibut free amino acid.", solvent: "Ethanol/water", keyReagents: "NaOH" }
      ],
      yield: "55–70 %",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Very High — simple amino-acid synthesis",
      solventsUsed: ["Ethanol", "Water", "Ethyl Acetate (wash)"],
      equipment: ["Standard reactor", "Hydrogenation capability (or Zn/HCl alternative)", "Crystallizer"],
      batchPlan: {
        targetBatch: "1 kg phenibut HCl",
        startingMaterial: "1.2 kg ethyl cinnamate",
        keyVolumes: ["Ethanol: 15 L", "Water: 10 L", "Nitromethane: 0.5 kg", "KOH: 0.4 kg"]
      },
      hazards: ["Nitromethane — explosive if concentrated and heated; keep dilute", "H₂/Raney Ni — pyrophoric catalyst"],
      vendorNotes: ["Cinnamate esters: Sigma, Penta", "Nitromethane: Specialty supplier"],
      estCostPerKg: "$2,500 – $4,500"
    },
    safety: {
      acuteToxicity: "LD50 rat ~900 mg/kg oral",
      chronicConcerns: ["Dependence syndrome with chronic dosing", "Withdrawal (baclofen-like) — potentially severe", "Synergistic sedation with alcohol/BZDs"],
      contraindications: ["Concurrent alcohol, benzodiazepines, opioids", "Pregnancy"],
      acuteSideEffects: ["Anxiolysis, sedation", "Euphoria", "At high dose: ataxia, respiratory depression"],
      ehsClassification: "GHS Cat. 4 oral"
    },
    publishedResearch: [
      { citation: "Lapin I. Phenibut (beta-phenyl-GABA): a tranquilizer and nootropic drug. CNS Drug Rev. 2001;7(4):471-81." }
    ],
    patentInfo: "Composition long expired (Russian 1960s).",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 15, evidenceQuality: 17, unmetMedicalNeed: 15, novelty: 6, universityInterest: 12 },
      harmBreakdown: { acuteToxicity: 6, chronicToxicity: 10, dependence: 15, schedulingRisk: 7 },
      netValue: 27,
      confidenceRationale: "HIGH — well-known pharmacology and dependence profile"
    },
    priorityTier: "Tier 3 — Research tool only",
    nextSteps: ["Dependence characterization study", "Reference standard for pharmacology studies"]
  },

  "f-phenibut": {
    id: "f-phenibut",
    name: "F-Phenibut",
    fullName: "β-(4-Fluorophenyl)-γ-aminobutyric acid",
    aliases: ["Fluorophenibut"],
    categoryId: "gabaergic",
    program: "Program J / Anxiety",
    formula: "C₁₀H₁₂FNO₂",
    molecularWeight: "197.21 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled.",
    benefitScore: 62,
    harmScore: 40,
    evidenceConfidence: "MODERATE",
    status: "Hold — Dependence Risk",
    statusColor: "orange",
    goDecision: "GO with caveats — research-use only",
    summary: "Fluorinated phenibut with 5–10× higher GABA-B/α2δ potency. Lower effective doses may reduce dependence — a hypothesis worth testing formally. Simple synthesis.",
    pros: ["Higher potency than phenibut", "Hypothesis: lower dose = lower dependence (testable)", "Simple synthesis"],
    cons: ["Same fundamental dependence mechanism as phenibut", "Less human data"],
    pharmacology: {
      targets: [
        { receptor: "GABA-B", activity: "Agonist", value: "5–10× phenibut" },
        { receptor: "α2δ calcium channel", activity: "Modulator", value: "Similar boost" }
      ],
      duration: "6–10 h",
      mechanism: "Enhanced-potency GABA-B + α2δ"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Same Michael addition route using 4-fluoro-ethyl cinnamate.",
      steps: [
        { step: "1", title: "Michael addition", description: "Nitromethane + 4-F-ethyl-cinnamate + KOH in EtOH.", solvent: "Ethanol", keyReagents: "4-F-ethyl-cinnamate" },
        { step: "2", title: "Nitro reduction", description: "Zn/HCl or H₂/Raney Ni in ethanol.", solvent: "Ethanol", keyReagents: "Zn/HCl" },
        { step: "3", title: "Ester hydrolysis + HCl salt", description: "NaOH/water, then HCl.", solvent: "Ethanol/water", keyReagents: "NaOH, HCl" }
      ],
      yield: "55–68 %",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Very High",
      solventsUsed: ["Ethanol", "Water", "Ethyl Acetate"],
      equipment: ["Same as phenibut"],
      batchPlan: { targetBatch: "1 kg F-phenibut HCl", startingMaterial: "1.3 kg 4-F-ethyl-cinnamate", keyVolumes: ["Ethanol: 15 L"] },
      hazards: ["Nitromethane + reduction hazards"],
      vendorNotes: ["4-F-cinnamate: Sigma, Combi-Blocks"],
      estCostPerKg: "$3,500 – $5,500"
    },
    safety: {
      acuteToxicity: "Assume phenibut-range",
      chronicConcerns: ["Same dependence mechanism"],
      contraindications: ["Same as phenibut"],
      acuteSideEffects: ["Same as phenibut but lower dose"],
      ehsClassification: "GHS Cat. 4 oral"
    },
    publishedResearch: [{ citation: "Limited — follow-on work to phenibut literature." }],
    patentInfo: "Composition open.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 14, evidenceQuality: 12, unmetMedicalNeed: 15, novelty: 10, universityInterest: 11 },
      harmBreakdown: { acuteToxicity: 6, chronicToxicity: 10, dependence: 15, schedulingRisk: 9 },
      netValue: 22,
      confidenceRationale: "MODERATE"
    },
    priorityTier: "Tier 3",
    nextSteps: ["Hypothesis test: dose-dependence characterization"]
  },

  "etifoxine": {
    id: "etifoxine",
    name: "Etifoxine / GRX-917",
    fullName: "Etifoxine (parent) and GRX-917 (deuterated analog)",
    aliases: ["Stresam", "Stresan"],
    categoryId: "gabaergic",
    program: "Program J / Anxiety",
    cas: "21715-46-8 (etifoxine)",
    formula: "C₁₇H₁₇ClN₂OS",
    molecularWeight: "332.85 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled in the U.S. Approved anxiolytic in France (Stresam). GRX-917 (atai/GABA Therapeutics deuterated analog) is in development.",
    benefitScore: 82,
    harmScore: 15,
    evidenceConfidence: "HIGH",
    status: "Strongly Favored",
    statusColor: "green",
    goDecision: "GO — cleanest anxiety research lane",
    summary: "Non-benzodiazepine anxiolytic acting via TSPO (translocator protein) activation → enhanced neurosteroid synthesis (allopregnanolone) → GABA-A positive modulation. Approved in France as Stresam for 40+ years. GRX-917 is the deuterated clinical candidate (atai). Minimal sedation, minimal amnesia, no classical benzodiazepine dependence.",
    pros: [
      "40+ years approved use in France — extensive human safety data",
      "Non-benzodiazepine mechanism → no classic dependence/withdrawal",
      "Endogenous neurosteroid enhancement = physiological anxiolysis",
      "Active clinical development (atai/GABA Therapeutics GRX-917)",
      "University-friendly: clean mechanism, strong safety record"
    ],
    cons: ["Rare hepatotoxicity reports (~0.01 % in surveillance)", "Rare hypersensitivity reactions"],
    pharmacology: {
      targets: [
        { receptor: "TSPO (translocator protein)", activity: "Agonist", value: "Activates neurosteroidogenesis" },
        { receptor: "Allopregnanolone (endogenous)", activity: "Indirect increase", value: "Metabolite-driven anxiolysis" },
        { receptor: "GABA-A β2/β3", activity: "Positive modulator (indirect via neurosteroids)", value: "Downstream" }
      ],
      duration: "6–12 h (etifoxine); extended for GRX-917 deuterated form",
      metabolism: "Hepatic; GRX-917 deuteration slows metabolism for extended action",
      mechanism: "TSPO activation → cholesterol transport into mitochondria → allopregnanolone synthesis → GABA-A PAM → anxiolysis without classic BZD binding"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Multi-step from 2-amino-4-chlorothiophenol + aldehyde/ketone + amine. Cyclization to benzothiazine.",
      steps: [
        { step: "1", title: "Benzothiazine intermediate", description: "2-Amino-4-chlorothiophenol + ketone under acidic conditions → benzothiazine.", solvent: "Ethanol or Toluene (substitute: Heptane with ethanol co-solvent)", keyReagents: "2-Amino-4-Cl-thiophenol, ketone" },
        { step: "2", title: "Side chain installation", description: "Alkylation with ethyl bromide-type reagent.", solvent: "EtOAc", keyReagents: "Ethyl bromide, base" },
        { step: "3", title: "Final coupling", description: "Amide coupling + purification.", solvent: "THF/EtOAc", keyReagents: "Coupling reagents" },
        { step: "4", title: "Crystallization", description: "Ethanol/heptane.", solvent: "Ethanol/Heptane", keyReagents: "Ethanol, heptane" }
      ],
      yield: "30–45 %",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Moderate — established industrial synthesis by Biocodex (Stresam manufacturer)",
      solventsUsed: ["Ethanol", "Ethyl Acetate", "THF", "Heptane"],
      equipment: ["Multi-step reactor", "Crystallizer"],
      batchPlan: { targetBatch: "1 kg etifoxine HCl", startingMaterial: "Intermediate-specific", keyVolumes: ["EtOH: 20 L", "EtOAc: 15 L"] },
      hazards: ["Thiol starting material — malodor; pyrophoric residues possible"],
      vendorNotes: ["2-Amino-4-chlorothiophenol: Sigma, Combi-Blocks"],
      estCostPerKg: "$22,000 – $32,000"
    },
    safety: {
      acuteToxicity: "LD50 rat ~900 mg/kg oral",
      chronicConcerns: ["Rare hepatotoxicity", "Rare hypersensitivity / DRESS-like reactions"],
      contraindications: ["Hepatic disease", "Pregnancy / lactation", "Hypersensitivity history"],
      acuteSideEffects: ["Drowsiness (mild)", "Rare rash"],
      ehsClassification: "GHS Cat. 4 oral"
    },
    publishedResearch: [
      { citation: "Nuss P et al. Etifoxine efficacy and safety profile in adjustment disorder. CNS Drugs. 2010." },
      { citation: "GABA Therapeutics / atai. GRX-917 clinical development." }
    ],
    patentInfo: "Etifoxine composition long expired (Biocodex 1960s). Deuterated GRX-917 patents held by atai.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 22, evidenceQuality: 20, unmetMedicalNeed: 20, novelty: 10, universityInterest: 10 },
      harmBreakdown: { acuteToxicity: 3, chronicToxicity: 7, dependence: 1, schedulingRisk: 4 },
      netValue: 67,
      confidenceRationale: "HIGH — extensive human use; clean mechanism"
    },
    priorityTier: "Tier 1 — Lead anxiety candidate",
    nextSteps: ["Reference standard production", "University partner for neurosteroid research", "Explore deuteration strategy"]
  },

  "krm-ii-81": {
    id: "krm-ii-81",
    name: "KRM-II-81",
    fullName: "KRM-II-81 (α2/3-selective GABA-A PAM, imidazodiazepine)",
    aliases: ["MP-III-80 analog"],
    categoryId: "gabaergic",
    program: "Program J / Anxiety",
    formula: "~C₂₁H₁₈BrN₄O₂ (imidazodiazepine core)",
    molecularWeight: "~454 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Imidazodiazepine class — GABA-A PAM mechanism shared with benzodiazepines. Scheduling-trajectory consideration.",
    benefitScore: 80,
    harmScore: 25,
    evidenceConfidence: "HIGH",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — lead α2/3-selective anxiety tool",
    summary: "α2/α3-selective GABA-A positive allosteric modulator. Classic benzodiazepines are non-selective across α1-α5 subunits; α1 drives sedation and abuse, α2/α3 drive anxiolysis. KRM-II-81 selectivity produces anxiolytic-like effects preclinically with minimal sedation/motor impairment. Published 2024 review: robust preclinical profile as anxiolytic without BZD baggage.",
    pros: [
      "Subtype-selective — anxiolysis without α1-mediated sedation/abuse",
      "Validated preclinical anxiolytic profile",
      "Strong publishing record from Cook/Witkin labs",
      "Clear 'safer benzodiazepine' positioning for universities"
    ],
    cons: ["Multi-step synthesis", "Imidazodiazepine structural class attracts scheduling attention", "Limited human data"],
    pharmacology: {
      targets: [
        { receptor: "GABA-A α2/α3-containing", activity: "Positive allosteric modulator (PAM)", value: "Selective vs α1" },
        { receptor: "GABA-A α1", activity: "Minimal activity", value: "Avoids sedation" }
      ],
      duration: "~6–8 h estimated",
      mechanism: "α2/α3-selective GABA-A PAM — anxiolysis without sedation/amnesia/abuse"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Multi-step imidazodiazepine synthesis per Cook lab published routes. Key steps include benzimidazole construction, cyclization to diazepine, and selective functionalization.",
      steps: [
        { step: "1", title: "Benzimidazole core", description: "From 2-amino-benzophenone + glycine-derived reagent.", solvent: "THF/EtOAc", keyReagents: "Amine + acid chloride" },
        { step: "2", title: "Imidazole ring closure", description: "Cyclization under acid or transition-metal catalysis.", solvent: "EtOAc", keyReagents: "Acid catalyst" },
        { step: "3", title: "Final functionalization", description: "Install bromo + ester groups.", solvent: "EtOAc", keyReagents: "NBS, esterification" },
        { step: "4", title: "Crystallization", description: "Recrystallize from EtOAc/heptane.", solvent: "EtOAc/Heptane", keyReagents: "EtOAc, heptane" }
      ],
      yield: "10–20 % over 5–7 steps",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Moderate-Low — multi-step requires significant process development",
      solventsUsed: ["Ethyl Acetate", "THF", "Methanol", "Ethanol", "Heptane"],
      equipment: ["Multi-stage reactor", "Flash chromatography"],
      batchPlan: { targetBatch: "100 g first target", startingMaterial: "Varies by route", keyVolumes: ["Multi-step: 50–100 L total solvent"] },
      hazards: ["Standard multi-step hazards"],
      vendorNotes: ["Intermediates: Sigma, Combi-Blocks, Matrix"],
      estCostPerKg: "$65,000 – $95,000 at 100-g scale"
    },
    safety: {
      acuteToxicity: "Favorable in preclinical",
      chronicConcerns: ["Long-term α2/3 PAM effects unknown", "Possible mild dependence at high doses"],
      contraindications: ["Pregnancy"],
      acuteSideEffects: ["Anxiolysis without sedation (rodent)", "No motor impairment at anxiolytic doses"],
      ehsClassification: "GHS Cat. 4 oral (suspected)"
    },
    publishedResearch: [
      { citation: "Witkin JM, Cerne R, Davis PR, Smith JL, Ping-Cui H, Fuchs PN, Tiruveedhula VVNPB, Rashid F, Obradovic AL, Golani LK, Johnson EM, Cook JM, Methuku KR. The α2,3-selective potentiator of GABA(A) receptors, KRM-II-81, reduces nociceptive-associated behaviors induced by formalin and spinal nerve ligation in rats. Pharmacol Biochem Behav. 2024." }
    ],
    patentInfo: "UW Milwaukee / Cook lab patents; licensed to RespireRx / BrainsGen.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 22, evidenceQuality: 18, unmetMedicalNeed: 20, novelty: 14, universityInterest: 6 },
      harmBreakdown: { acuteToxicity: 4, chronicToxicity: 7, dependence: 5, schedulingRisk: 9 },
      netValue: 55,
      confidenceRationale: "HIGH — robust preclinical program"
    },
    priorityTier: "Tier 2",
    nextSteps: ["IP landscape review (UW/RespireRx)", "University partner for SAR extension"]
  },

  "salvinorin-a-analogs": {
    id: "salvinorin-a-analogs",
    name: "Salvinorin A Analogs",
    fullName: "Salvinorin A and C-2 modified analogs (EOM-Salvinorin B, etc.)",
    aliases: ["KOR agonists"],
    categoryId: "pain",
    program: "Program D / Pain",
    formula: "C₂₃H₂₈O₈ (parent Salvinorin A)",
    molecularWeight: "432.46 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Salvinorin A not federally scheduled; some U.S. states have scheduled it (CA, OH, WI partial). Novel analogs generally unscheduled.",
    benefitScore: 82,
    harmScore: 28,
    evidenceConfidence: "HIGH",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — KOR analgesia is major pharma target",
    summary: "Salvinorin A (from Salvia divinorum) is the most potent natural hallucinogen known — active at microgram doses — and is the first non-nitrogenous opioid receptor ligand discovered, acting exclusively on KOR. 2024 PMC research screened C-2 analogs for selective KOR agonism with reduced hallucinogenic effects. Major target for non-opioid analgesia and anti-itch research.",
    pros: [
      "KOR-selective analgesia — no respiratory depression, different addiction liability",
      "Non-nitrogenous scaffold — unique medicinal chemistry space",
      "Validated analgesic + anti-pruritus mechanism",
      "C-2 analogs with reduced hallucinogenic profile available",
      "Strong academic research interest"
    ],
    cons: ["Complex terpenoid scaffold — synthesis is demanding", "Some KOR agonists produce dysphoria (class effect)", "State-level Salvia scheduling complexity"],
    pharmacology: {
      targets: [
        { receptor: "κ-opioid receptor (KOR)", activity: "Selective agonist", value: "Ki < 1 nM (parent Salvinorin A)" },
        { receptor: "μ/δ-opioid", activity: "Negligible", value: "Exclusive KOR" }
      ],
      duration: "Short (15–30 min) for parent; analogs variable",
      mechanism: "Selective KOR agonism — analgesia without mu-opioid respiratory depression; mechanism distinct from all mu agonists"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Isolation from Salvia divinorum biomass for parent; semi-synthetic C-2 analogs via functional group interconversion (e.g., EOM-salvinorin B from salvinorin A hydrolysis → methyl enol ether formation).",
      steps: [
        { step: "1", title: "Salvinorin A isolation", description: "Acetone extraction of dried Salvia divinorum leaf; silica chromatography.", solvent: "Acetone → EtOAc/Heptane", keyReagents: "Acetone, silica" },
        { step: "2", title: "Hydrolysis to Salvinorin B", description: "Controlled basic hydrolysis of C-2 acetate.", solvent: "Methanol/aqueous", keyReagents: "K₂CO₃" },
        { step: "3", title: "C-2 analog synthesis", description: "Methyl enol ether (EOM-Salvinorin B) or other C-2 modifications via standard organic chemistry.", solvent: "EtOAc/THF", keyReagents: "Various" },
        { step: "4", title: "Recrystallization", description: "From ethanol or EtOAc/heptane.", solvent: "Ethanol or EtOAc/Heptane", keyReagents: "Ethanol, heptane" }
      ],
      yield: "~0.2–0.4 % isolation yield from dry leaf; ~30–60 % for semi-synthetic modifications",
      purityTarget: "≥ 98 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Very Low for bulk — biomass is limiting. 10–50 g realistic target.",
      solventsUsed: ["Acetone", "Ethyl Acetate", "Methanol", "Ethanol", "Heptane"],
      equipment: ["Large extraction vessel", "Chromatography skid"],
      batchPlan: { targetBatch: "50 g Salvinorin A + 10 g EOM-Salvinorin B", startingMaterial: "~15 kg dried Salvia divinorum leaf", keyVolumes: ["Acetone: 60 L", "EtOAc: 30 L"] },
      hazards: ["Standard biomass extraction", "Acetone flammable — ATEX"],
      vendorNotes: ["Salvia divinorum leaf: specialty ethnobotanical suppliers (legal varies by state)"],
      estCostPerKg: "Not bulk-feasible; $120,000+ per 100 g"
    },
    safety: {
      acuteToxicity: "Extremely low — active at microgram doses, wide safety margin",
      chronicConcerns: ["Dysphoria (KOR class effect)", "No respiratory depression"],
      contraindications: ["Psychosis spectrum", "Uncontrolled depression"],
      acuteSideEffects: ["Dissociation (parent)", "Analgesia", "Anti-pruritus"],
      ehsClassification: "GHS Cat. 4 oral"
    },
    publishedResearch: [
      { citation: "Discovery of Novel, Selective, and Nonbasic Agonists for the Kappa-Opioid Receptor Determined by Salvinorin A-Based Virtual Screening. PMC. 2024.", pmc: "PMC11345774" },
      { citation: "Roth BL, Baner K et al. Salvinorin A: a potent naturally occurring nonnitrogenous kappa opioid selective agonist. Proc Natl Acad Sci U S A. 2002;99(18):11934-9." }
    ],
    patentInfo: "Natural product — parent not patentable. C-2 modified analogs patentable; several filings in progress.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 22, evidenceQuality: 19, unmetMedicalNeed: 20, novelty: 15, universityInterest: 6 },
      harmBreakdown: { acuteToxicity: 5, chronicToxicity: 8, dependence: 4, schedulingRisk: 11 },
      netValue: 54,
      confidenceRationale: "HIGH — natural product + growing analog literature"
    },
    priorityTier: "Tier 2",
    nextSteps: ["State-scheduling review", "Partner with Roth/Cohen labs (UNC, Stanford)", "Produce EOM-Salvinorin B research tool"]
  },

  "sigma-1-modulators": {
    id: "sigma-1-modulators",
    name: "Sigma-1 Receptor Modulators",
    fullName: "Sigma-1 receptor antagonists and modulators (class)",
    aliases: ["S1R ligands"],
    categoryId: "pain",
    program: "Program D / Pain",
    formula: "Class — various",
    molecularWeight: "Varies",
    schedulingStatus: "unscheduled",
    schedulingNote: "Sigma-1 ligands generally not scheduled. Specific compounds may have FDA/Rx status (e.g., S1RA/E-52862 in development).",
    benefitScore: 78,
    harmScore: 16,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — cutting-edge non-opioid pain target",
    summary: "Sigma-1 receptors are intracellular chaperone proteins at the mitochondria-associated ER membrane. Sigma-1 antagonism modulates central sensitization — underlying chronic pain, fibromyalgia, neuropathic pain — without opioid receptor engagement. Emerging 2023 PMC research highlights sigma receptors as promising pain targets.",
    pros: ["Novel non-opioid analgesic mechanism", "Active academic and industry development", "Low scheduling risk", "Class provides multiple compound opportunities"],
    cons: ["No single lead compound established", "Requires compound selection + synthesis program", "Off-target activity common in early ligands"],
    pharmacology: {
      targets: [
        { receptor: "Sigma-1", activity: "Antagonist/modulator", value: "Compound-specific" },
        { receptor: "Sigma-2", activity: "Varies", value: "Often co-active" }
      ],
      duration: "Varies",
      mechanism: "Central sensitization blockade — distinct from opioid analgesia; anti-neuropathic, anti-allodynic"
    },
    labSynthesis: {
      title: "Compound-Specific",
      overview: "Wohler to select 2–3 lead compounds (S1RA/E-52862 analog, BD-1063 analog) from published SAR literature for reference-standard synthesis. Each will have a tailored synthesis plan based on individual structure.",
      steps: [{ step: "1", title: "Compound selection", description: "Scientific committee to prioritize 2–3 leads from 2023/2024 sigma-1 literature.", solvent: "Literature review", keyReagents: "N/A" }],
      yield: "TBD",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Compound-Specific",
      feasibility: "TBD",
      solventsUsed: ["TBD — most sigma ligands compatible with Wohler standard solvents"],
      equipment: ["Standard"],
      batchPlan: { targetBatch: "100 g per lead compound", startingMaterial: "Varies", keyVolumes: ["TBD"] },
      hazards: ["Compound-specific"],
      vendorNotes: ["Sigma, Combi-Blocks"],
      estCostPerKg: "TBD"
    },
    safety: {
      acuteToxicity: "Class generally well-tolerated in preclinical",
      chronicConcerns: ["Varies by compound"],
      contraindications: ["TBD"],
      acuteSideEffects: ["Mild; no respiratory depression expected"],
      ehsClassification: "TBD"
    },
    publishedResearch: [
      { citation: "The Emerging Role of Sigma Receptors in Pain Medicine. PMC. 2023.", pmc: "PMC10460634" }
    ],
    patentInfo: "Active patent landscape; several industry programs. Wohler can produce research-scale under academic exception.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 22, evidenceQuality: 16, unmetMedicalNeed: 22, novelty: 12, universityInterest: 6 },
      harmBreakdown: { acuteToxicity: 3, chronicToxicity: 5, dependence: 2, schedulingRisk: 6 },
      netValue: 62,
      confidenceRationale: "MODERATE — strong class rationale; compound-specific data needed"
    },
    priorityTier: "Tier 2 — Class selection required",
    nextSteps: ["Literature review to select 2–3 lead compounds", "IP landscape review", "Partner identification"]
  },

  "taar1-agonists": {
    id: "taar1-agonists",
    name: "TAAR1 Agonists",
    fullName: "Trace Amine-Associated Receptor 1 Agonists (class)",
    aliases: ["Ulotaront-like", "Ralmitaront-like"],
    categoryId: "addiction",
    program: "Program J / Addiction",
    formula: "Class — various",
    molecularWeight: "Varies",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Some class members (ulotaront, ralmitaront) are in clinical development.",
    benefitScore: 80,
    harmScore: 16,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — leverage Wohler's tryptamine expertise",
    summary: "TAAR1 is a novel target for addiction and psychosis, largely ignored until Sunovion developed ulotaront (Phase III for schizophrenia, failed 2023) and ralmitaront (depression). TAAR1 agonism modulates dopaminergic, serotonergic, and glutamatergic neurotransmission — reducing hyperdopaminergic states underlying stimulant addiction and psychosis. Several TAAR1 agonists are tryptamine-derived — directly leveraging Wohler's expertise.",
    pros: [
      "Cutting-edge target",
      "Wohler tryptamine synthesis expertise is directly relevant",
      "Strong mechanistic rationale for stimulant addiction + psychosis research",
      "Unscheduled class",
      "Several published TAAR1 ligand structures available"
    ],
    cons: ["Ulotaront failed Phase III 2023 — target validation questioned", "No single lead compound established for research"],
    pharmacology: {
      targets: [
        { receptor: "TAAR1", activity: "Agonist", value: "Compound-specific EC50" },
        { receptor: "5-HT1A (secondary, ulotaront)", activity: "Partial agonist", value: "Contributes to antipsychotic activity" }
      ],
      duration: "Varies",
      mechanism: "TAAR1 activation modulates monoamine release → reduces hyperdopaminergic state"
    },
    labSynthesis: {
      title: "Compound-Specific",
      overview: "Wohler to select TAAR1 research tools from published literature. β-PEA analogs and simple tryptamine-derived ligands have published structures.",
      steps: [{ step: "1", title: "Compound selection", description: "Select 2–3 lead tools from SAR literature.", solvent: "N/A", keyReagents: "N/A" }],
      yield: "TBD",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Compound-Specific",
      feasibility: "Moderate — tryptamine-derived analogs leverage Wohler infrastructure",
      solventsUsed: ["THF", "Ethanol", "Ethyl Acetate", "Heptane"],
      equipment: ["Tryptamine synthesis train"],
      batchPlan: { targetBatch: "100 g per compound", startingMaterial: "Varies", keyVolumes: ["TBD"] },
      hazards: ["Compound-specific"],
      vendorNotes: ["Sigma, Combi-Blocks"],
      estCostPerKg: "TBD"
    },
    safety: {
      acuteToxicity: "Class generally well-tolerated preclinical; human data from ulotaront/ralmitaront available",
      chronicConcerns: ["TBD"],
      contraindications: ["TBD"],
      acuteSideEffects: ["Reduced dopaminergic overactivity"],
      ehsClassification: "TBD"
    },
    publishedResearch: [
      { citation: "Correll CU et al. Efficacy and safety of ulotaront in patients with schizophrenia. N Engl J Med. 2020." },
      { citation: "Heffernan MLR et al. Ulotaront: A TAAR1 Agonist for the Treatment of Schizophrenia. ACS Med Chem Lett. 2022." }
    ],
    patentInfo: "Sunovion holds ulotaront IP. Research-tool β-PEA analogs are composition-open.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 22, evidenceQuality: 17, unmetMedicalNeed: 20, novelty: 15, universityInterest: 6 },
      harmBreakdown: { acuteToxicity: 3, chronicToxicity: 6, dependence: 2, schedulingRisk: 5 },
      netValue: 64,
      confidenceRationale: "MODERATE"
    },
    priorityTier: "Tier 2",
    nextSteps: ["Select 2–3 research-tool compounds", "Leverage existing tryptamine synthesis infrastructure", "Partner with stimulant-addiction lab"]
  }
};